<template>
  <v-container
    id="wizard"
    tag="section"
  >
    <base-material-wizard
      v-model="tab"
      :available-steps="availableSteps"
      :items="tabs"
      :loading="loading"
      class="mx-auto"
      color="blue-grey"
      @click:next="next"
      @click:prev="tab--"
      @tab:change="save"
    >
      <v-tab-item>
        <form
          action=""
          data-vv-scope="productTemplate"
        >
          <div class="text-center display-1 font-weight-light mb-6">
            Specify Product Template Requirements
          </div>
          <product-template-edit
            v-model="editedItem"
            class="pa-0"
            :form-errors="formErrors"
            @change="onTemplateChange"
          />
        </form>
      </v-tab-item>
      <v-tab-item>
        <form
          action=""
          data-vv-scope="productTemplateSection"
        >
          <div class="text-center display-1 font-weight-light mb-6">
            Specify the sections to group the questions by.
          </div>
          <product-template-section-tab
            v-if="productTemplateId"
            class="pa-0"
            :product-template-id="productTemplateId"
            :form-errors="formErrors"
            @item:changed="onSectionChange"
          />
        </form>
      </v-tab-item>
      <v-tab-item>
        <form
          action=""
          data-vv-scope="productTemplateDetail"
        >
          <div class="text-center display-1 font-weight-light mb-6">
            Specify details about the questions to ask.
          </div>
          <product-template-detail-tab
            v-if="hasProductTemplateSections"
            class="pa-0"
            :product-template-id="productTemplateId"
            :form-errors="formErrors"
            :section-change-key="sectionChangeKey"
          />
        </form>
      </v-tab-item>
      <template v-slot:buttons>
        <v-btn
          color="blue-grey darken-1"
          @click="finished"
        >
          Close
        </v-btn>
      </template>
    </base-material-wizard>
    <base-material-snackbar
      v-model="displayError"
      type="error"
      v-bind="{
        top: true,
        right: true
      }"
    >
      {{ errorMessage }}
    </base-material-snackbar>
  </v-container>
</template>

<script>
  import TroodyApi from '@/services/troody/TroodyApi'
  import { extractFormErrors, getErrorTitle } from '@/services/ErrorHandlers'

  export default {
    name: 'ProductTemplateWizard',

    components: {
      ProductTemplateEdit: () => import('./ProductTemplateEdit'),
      ProductTemplateSectionTab: () => import('./ProductTemplateSectionTab'),
      ProductTemplateDetailTab: () => import('./ProductTemplateDetailTab'),
    },

    props: {
      editedItem: {
        type: Object,
        default: null,
      },
    },

    data: () => ({
      tab: 0,

      productTemplateRecord: null,
      productTemplateType: null,

      loading: false,
      errorMessage: null,
      displayError: false,

      productTemplateDirty: false,

      formErrors: {},

      sectionChangeKey: 0,

      productTemplateResource: new TroodyApi('productTemplate/'),
    }),

    computed: {
      tabs () {
        return ['Template', 'Sections', 'Details']
      },

      scope () {
        if (this.tab === 0) return 'productTemplate'
        else if (this.tab === 1) return 'productTemplateSection'
        return 'productTemplateDetail'
      },

      availableSteps () {
        const steps = [0]

        if (this.productTemplateComplete()) {
          steps.push(1)
        }

        if (this.hasProductTemplateSections) {
          steps.push(2)
        }

        return steps
      },

      dataDirty () {
        return this.productTemplateDirty
      },

      productTemplateId () {
        return (this.productTemplateRecord || {}).id
      },

      hasProductTemplateSections () {
        return this.productTemplateId
      },
    },

    watch: {
      editedItem (val) {
        this.initialize()
      },
    },

    created () {
      this.initialize()
    },

    methods: {
      initialize () {
        this.productTemplateRecord = Object.assign({}, this.editedItem)
        this.productTemplateType = this.productTemplateRecord.productTemplateType

        this.productTemplateDirty = false

        this.formErrors = {}

        this.tab = 0
      },

      finished () {
        this.save()
          .then(() => {
            this.formErrors = {}
            this.$emit('wizard:finished')
          })
      },

      next () {
        this.loading = true
        this.save()
          .then(() => {
            this.loading = false
            if (this.tab === this.tabs.length - 1) {
              this.finished()
            } else {
              this.tab++
            }
          })
      },

      handleError (error) {
        this.loading = false
        this.formErrors = extractFormErrors(error)
        if (this.formErrors) {
          this.showError(getErrorTitle(error), 'Failed to save!')
        } else {
          this.formErrors = {}
          this.showError(error, 'Failed to save!')
        }
      },

      showError (error, msg) {
        console.log(error)
        this.errorMessage = (error || msg)
        this.displayError = true
      },

      saveProductTemplate () {
        const promise = new Promise((resolve, reject) => {
          console.log(this.productTemplateRecord)
          this.productTemplateResource.save(this.productTemplateRecord)
            .then((data) => {
              this.productTemplateRecord = data
              this.productTemplateDirty = false
              this.save() // Save anything left for the other steps
              this.$emit('producttemplate:saved', data)
              resolve(data)
            })
            .catch((error) => {
              this.tab = 0
              reject(error)
            })
        })

        return promise
      },

      save () {
        const promise = new Promise((resolve, reject) => {
          if (this.dataDirty) {
            if (this.productTemplateDirty) {
              this.saveProductTemplate()
                .then((data) => {
                  resolve(data)
                })
                .catch((error) => {
                  this.handleError(error)
                  reject(error)
                })
            }
          } else {
            resolve({ dataDirty: false })
          }
        })

        return promise
      },

      productTemplateComplete () {
        let complete = false

        if (this.productTemplateRecord) {
          if (this.productTemplateType === 'Standard') {
            complete = !!(this.productTemplateRecord.productTypeId && this.productTemplateRecord.platformId)
          } else {
            complete = !!(this.productTemplateRecord.productTypeId && this.productTemplateRecord.productId && this.productTemplateRecord.carrierId && this.productTemplateRecord.platformId)
          }
        }

        return complete
      },

      validateForm (scope) {
        return this.$validator.validateAll(scope)
      },

      onSectionChange (data) {
        this.sectionChangeKey++
      },

      onTemplateChange (data, productTemplateType) {
        this.productTemplateType = productTemplateType
        this.productTemplateRecord = Object.assign({}, data)
        this.productTemplateDirty = true
        this.$emit('producttemplate:changed', this.productTemplateRecord)
      },
    },
  }
</script>
